import { useQuery } from '@tanstack/react-query';
import { type FC, type ReactNode, useId, useMemo, useState } from 'react';

import { _getCategories } from '@/api/address-book/queries';
import {
  Alert,
  Button,
  Loader,
  RadioCard,
  RadioGroup,
  theme,
  Tooltip,
  Typography,
} from '@/shared/design-system';
import {
  DeniedReasonTrigger,
  Permission,
  useAddressBookCategoryScope,
  useCan,
} from '@/shared/modules/permissions';

import {
  ALL_CATEGORIES_DESCRIPTION,
  ALL_CATEGORIES_HELD_NOTE,
  ALL_CATEGORIES_LABEL,
  ALL_CATEGORIES_LOCKED_ROLE_REASON,
  ALL_CATEGORIES_REQUEST_HELP,
  ALL_CATEGORIES_REQUESTED_NOTE,
  CATEGORIES_LOAD_ERROR,
  CATEGORIES_READ_DENIED_REASON,
  CATEGORY_SCOPE_HELP,
  CATEGORY_SCOPE_TITLE,
  GAMMA_SWITCH_WARNING,
  isCategoryScopeDirty,
  NO_CATEGORIES_MESSAGE,
  REQUEST_ALL_CATEGORIES_LABEL,
  resolveCategoryScopeState,
  SAVE_CATEGORIES_LABEL,
  SELECTED_CATEGORIES_DESCRIPTION,
  SELECTED_CATEGORIES_LABEL,
  SELECTED_CATEGORIES_LOCKED_ROLE_REASON,
  SELECTED_REQUEST_HELP,
  SELECTED_REQUESTED_NOTE,
  selectedCountLabel,
  splitStaleCategoryIds,
  staleCategoriesNote,
  staleCategoriesWarning,
  SWITCH_TO_SELECTED_LABEL,
  toCategoryScopePayload,
  toggleCategoryScope,
} from '../../category-scope';
import type { GetRoleRes } from '../../types';
import { CategoryScopeTree } from './category-scope-tree';
import { ControlRoot, ModeOptions, PanelFooter, RequestActions, RequestPanel } from './styled';

const MODE = { all: 'all', selected: 'selected' } as const;

type ScopeView = (typeof MODE)[keyof typeof MODE];

type CategoryScopeDraft = {
  savedKey: string;
  picked: readonly string[];
  mode: ScopeView;
  requested: ScopeView | null;
};

export type CategoryScopeControlProps = {
  role: GetRoleRes;
  scopable: boolean;
  canManage: boolean;
  queryIdentity: string;
  saving: boolean;
  onRequestAll: () => Promise<void>;
  onRequestSelected: () => Promise<void>;
  onSave: (_resourceIds: string[]) => Promise<void>;
  requestAllBlockedReason?: string;
};

const succeeds = async (action: () => Promise<void>) => {
  try {
    await action();

    return true;
  } catch {
    return false;
  }
};

export const CategoryScopeControl: FC<CategoryScopeControlProps> = ({
  role,
  scopable,
  canManage,
  queryIdentity,
  saving,
  onRequestAll,
  onRequestSelected,
  onSave,
  requestAllBlockedReason,
}) => {
  const titleId = useId();
  const state = useMemo(() => resolveCategoryScopeState({ role, scopable }), [role, scopable]);
  const canReadCategories = useCan(Permission.AddressBookView) || canManage;
  const { isScoped: callerIsScoped } = useAddressBookCategoryScope();
  const [edit, setEdit] = useState<CategoryScopeDraft | null>(null);
  const [switchPending, setSwitchPending] = useState(false);

  const savedIds: readonly string[] = state.mode === 'hidden' ? [] : state.savedIds;
  const savedKey = [role.id, ...(state.mode === MODE.selected ? savedIds : [])].join('\u001f');
  const draft: CategoryScopeDraft =
    edit?.savedKey === savedKey
      ? edit
      : {
          savedKey,
          picked: savedIds,
          mode: state.mode === MODE.all ? MODE.all : MODE.selected,
          requested: null,
        };
  const { picked } = draft;

  const allBlockedReason =
    state.mode === MODE.selected
      ? requestAllBlockedReason ||
        (state.canRequestAll ? undefined : ALL_CATEGORIES_LOCKED_ROLE_REASON)
      : undefined;
  const allDisabled = state.mode === MODE.selected && (Boolean(allBlockedReason) || !canManage);
  const selectedBlockedReason =
    state.mode === MODE.all && !state.canRequestSelected
      ? SELECTED_CATEGORIES_LOCKED_ROLE_REASON
      : undefined;
  const selectedDisabled =
    state.mode === MODE.all && (Boolean(selectedBlockedReason) || !canManage);
  const view = allDisabled ? MODE.selected : selectedDisabled ? MODE.all : draft.mode;
  const switchingToSelected = state.mode === MODE.all && view === MODE.selected;

  const categories = useQuery({
    ..._getCategories(queryIdentity),
    enabled: (state.mode === MODE.selected || switchingToSelected) && canReadCategories,
  });

  if (state.mode === 'hidden') return null;

  const updateDraft = (patch: Partial<Omit<CategoryScopeDraft, 'savedKey'>>) =>
    setEdit({ ...draft, ...patch });
  const latchRequested = (requested: ScopeView | null) =>
    setEdit((current) => (current?.savedKey === savedKey ? { ...current, requested } : current));

  const requestAll = async () => {
    updateDraft({ requested: MODE.all });
    try {
      await onRequestAll();
    } catch {
      // The role editor's request mutation already toasts why the request failed.
      latchRequested(null);
    }
  };

  const tree = categories.data ?? [];
  const payload = toCategoryScopePayload(picked, tree);
  const staleCount = splitStaleCategoryIds(picked, tree).stale.length;
  // The server keeps every row a caller without full view cannot open, a deleted category's too.
  const dropsStaleOnSave = canManage && !callerIsScoped;
  const dirty = categories.isSuccess && isCategoryScopeDirty(savedIds, payload);
  const selectedRequested = state.mode === MODE.all && draft.requested === MODE.selected;
  const switchLocked = state.mode === MODE.all && (switchPending || selectedRequested);

  const switchToSelected = async () => {
    setSwitchPending(true);
    const switched = (await succeeds(() => onSave(payload))) && (await succeeds(onRequestSelected));
    setSwitchPending(false);
    if (switched) latchRequested(MODE.selected);
  };

  const withReason = (option: ReactNode, reason: string | undefined) =>
    reason ? (
      <Tooltip title={reason}>
        <DeniedReasonTrigger denied style={{ display: 'block' }}>
          {option}
        </DeniedReasonTrigger>
      </Tooltip>
    ) : (
      option
    );

  const countLine = (
    <Typography variant="body4" color={theme.colors.text.tertiary}>
      {selectedCountLabel(payload.length)}
    </Typography>
  );

  const saveFooter = (
    <PanelFooter>
      {countLine}
      {canManage && (
        <Button
          size="sm"
          disabled={!dirty || saving}
          onClick={() => succeeds(() => onSave(payload))}
        >
          {SAVE_CATEGORIES_LABEL}
        </Button>
      )}
    </PanelFooter>
  );

  const switchFooter = (
    <>
      {payload.length === 0 ? (
        <Alert variant="warning" size="md" description={selectedCountLabel(0)} />
      ) : (
        countLine
      )}
      <RequestActions>
        <Button size="sm" disabled={saving || switchLocked} onClick={switchToSelected}>
          {SWITCH_TO_SELECTED_LABEL}
        </Button>
        {selectedRequested && (
          <Typography variant="body4" color={theme.colors.text.tertiary}>
            {SELECTED_REQUESTED_NOTE}
          </Typography>
        )}
      </RequestActions>
    </>
  );

  const panel = () => {
    if (!canReadCategories) {
      return (
        <Typography variant="body4" color={theme.colors.text.secondary}>
          {CATEGORIES_READ_DENIED_REASON}
        </Typography>
      );
    }
    if (categories.isPending) return <Loader />;
    if (categories.isError) {
      return (
        <Typography variant="body4" color={theme.colors.text.secondary}>
          {CATEGORIES_LOAD_ERROR}
        </Typography>
      );
    }
    if (tree.length === 0) {
      return (
        <>
          <Typography variant="body4" color={theme.colors.text.secondary}>
            {NO_CATEGORIES_MESSAGE}
          </Typography>
          {switchingToSelected && switchFooter}
        </>
      );
    }

    return (
      <>
        {staleCount > 0 && (
          <Alert
            variant="warning"
            size="md"
            description={
              dropsStaleOnSave
                ? staleCategoriesWarning(staleCount)
                : staleCategoriesNote(staleCount)
            }
          />
        )}
        <CategoryScopeTree
          categories={tree}
          picked={new Set(picked)}
          editable={canManage && !saving && !switchLocked}
          onToggle={(id) => updateDraft({ picked: toggleCategoryScope(picked, tree, id) })}
        />
        {switchingToSelected ? switchFooter : saveFooter}
      </>
    );
  };

  const requestPanel = (
    <RequestPanel>
      <Typography variant="body4" color={theme.colors.text.secondary}>
        {ALL_CATEGORIES_REQUEST_HELP}
      </Typography>
      <RequestActions>
        <Button
          size="sm"
          disabled={draft.requested === MODE.all || saving || allDisabled}
          onClick={requestAll}
        >
          {REQUEST_ALL_CATEGORIES_LABEL}
        </Button>
        {draft.requested === MODE.all && (
          <Typography variant="body4" color={theme.colors.text.tertiary}>
            {ALL_CATEGORIES_REQUESTED_NOTE}
          </Typography>
        )}
      </RequestActions>
    </RequestPanel>
  );

  return (
    <ControlRoot data-category-scope role="group" aria-labelledby={titleId}>
      <Typography id={titleId} variant="body3" fontWeight={600} color={theme.colors.text.primary}>
        {CATEGORY_SCOPE_TITLE}
      </Typography>
      <Typography variant="body4" color={theme.colors.text.tertiary}>
        {CATEGORY_SCOPE_HELP}
      </Typography>
      <RadioGroup
        value={view}
        readOnly={!canManage}
        onValueChange={(value) =>
          updateDraft({ mode: value === MODE.all ? MODE.all : MODE.selected })
        }
      >
        <ModeOptions>
          {withReason(
            <RadioCard
              value={MODE.all}
              label={ALL_CATEGORIES_LABEL}
              description={
                state.mode === MODE.all ? ALL_CATEGORIES_HELD_NOTE : ALL_CATEGORIES_DESCRIPTION
              }
              disabled={allDisabled}
            />,
            allBlockedReason,
          )}
          {withReason(
            <RadioCard
              value={MODE.selected}
              label={SELECTED_CATEGORIES_LABEL}
              description={SELECTED_CATEGORIES_DESCRIPTION}
              disabled={selectedDisabled}
            />,
            selectedBlockedReason,
          )}
        </ModeOptions>
      </RadioGroup>
      {state.mode === MODE.selected && (view === MODE.all ? requestPanel : panel())}
      {switchingToSelected && (
        <>
          <Typography variant="body4" color={theme.colors.text.secondary}>
            {SELECTED_REQUEST_HELP}
          </Typography>
          {requestAllBlockedReason && (
            <Typography variant="body4" color={theme.colors.text.secondary}>
              {GAMMA_SWITCH_WARNING}
            </Typography>
          )}
          {panel()}
        </>
      )}
    </ControlRoot>
  );
};
